import {Record, Map} from 'immutable';
import moment from 'moment-timezone';

import {makeReducer, match} from 'web-app/util/redux';

import * as Actions from './actions';
import {convertPeriodsToDateMap} from './helpers';

interface OpenDateRange {
    from: Date;
    to?: Date;
}

export interface AttendanceScheduleState {
    readonly calendarActive: boolean;
    readonly fetchingFromREST: boolean;
    readonly enabledDates: Map<Date, boolean>;
    readonly dateSelection?: OpenDateRange;
}

export class AttendanceScheduleReducer extends Record<AttendanceScheduleState>({
    calendarActive: false,
    fetchingFromREST: false,
    enabledDates: Map(),
    dateSelection: undefined,
}) {}

export default makeReducer<AttendanceScheduleReducer>(
    [
        match(Actions.reset, () => new AttendanceScheduleReducer()),
        match(Actions.fetch, state => state.merge({fetchingFromREST: true})),
        match(Actions.fetchSuccess, state => state.merge({fetchingFromREST: false})),
        match(Actions.fetchFailed, state => state.merge({fetchingFromREST: false})),
        match(Actions.touchDate, (state, {payload}) =>
            state.merge({
                enabledDates: state.enabledDates.set(payload.date, !state.enabledDates.get(payload.date)),
            }),
        ),
        match(Actions.toggleDate, (state, {payload}) => {
            if (state.dateSelection) {
                const toggleTo = !state.enabledDates.get(state.dateSelection.from);
                return state.merge({
                    dateSelection: undefined,
                    enabledDates: (() => {
                        let enabledDates = state.enabledDates;
                        let from;
                        let to;

                        if (state.dateSelection.from < payload.date) {
                            from = moment(state.dateSelection.from).hours(12);
                            to = moment(payload.date).hours(12);
                        } else {
                            from = moment(payload.date).hours(12);
                            to = moment(state.dateSelection.from).hours(12);
                        }

                        if (from.isSame(to, 'day')) {
                            return enabledDates.set(from.toDate(), toggleTo);
                        }

                        while (from.isSameOrBefore(to, 'day')) {
                            enabledDates = enabledDates.set(from.toDate(), toggleTo);
                            from.add(1, 'day');
                        }

                        return enabledDates;
                    })(),
                });
            }
            return state.merge({
                dateSelection: {
                    from: payload.date,
                    to: payload.date,
                },
            });
        }),
        match(Actions.loadPeriodsFromApi, (state, {payload}) =>
            state.merge({
                enabledDates: convertPeriodsToDateMap(payload.periods),
            }),
        ),
        match(Actions.dayMouseOver, (state, {payload}) => {
            if (state.dateSelection) {
                return state.merge({
                    dateSelection: {
                        from: state.dateSelection.from,
                        to: payload.date,
                    },
                });
            }
            return state;
        }),
        match(Actions.activateCalendar, state =>
            state.merge({
                calendarActive: true,
            }),
        ),
        match(Actions.deactivateCalendar, state =>
            state.merge({
                calendarActive: false,
            }),
        ),
    ],
    new AttendanceScheduleReducer(),
);
